import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from '..';
import { ICompanyOnboardingStatus } from '../../resources/interfaces';

interface IInitialState {
  SHOW_TOP_LOADING: boolean;
  SESSION_EXPIRED: boolean;
  SESSION_SUPERSEDED: boolean;
  REDIRECT_URL: string;
  COMPANY_ONBOARDING_STATUS: ICompanyOnboardingStatus
  PENDING_ACTIONS: string[];
  TERMS_AND_CONDITIONS_DISMISSED: boolean | null;
  WORKSPACE_SETUP_BANNER_MINIMIZED: boolean;
}

const initialState: IInitialState = {
  SHOW_TOP_LOADING: false,
  SESSION_EXPIRED: false,
  SESSION_SUPERSEDED: false,
  REDIRECT_URL: '',
  PENDING_ACTIONS: [],
  TERMS_AND_CONDITIONS_DISMISSED: null,
  WORKSPACE_SETUP_BANNER_MINIMIZED: false,
  COMPANY_ONBOARDING_STATUS: {
    companyInfo: {
      id: undefined,
      name: undefined,
      setupCompleted: false,
      signedOn: '',
      subscriptionPlanId: null,
      subscriptionPlanName: null,
      termsAndConditionsAccepted: null,
      termsAndConditionsAcceptedBy: '',
      termsAndConditionsAcceptedDateAndTime: '',
      type: '',
      trialExpiry: null
    },
    completionPercentage: 0,
    notificationSettings: {
      configured: false,
      emailAddress: '',
      emailEnabled: false,
      message: '',
      phoneEnabled: false,
      phoneNumber: ''
    },
    onboardingComplete: undefined,
    payoutAccount: {
      accountName: '',
      accountNumber: '',
      accountProvider: '',
      accountType: '',
      configured: false,
      message: ''
    },
    pendingActions: [],
    securitySettings: {
      configured: false,
      enabled: false,
      message: '',
      mode: ''
    }
  }
};

const globalSlice = createSlice({
  name: 'globals',
  initialState,
  reducers: {
    SHOW_TOP_LOADING: (state) => {
      state.SHOW_TOP_LOADING = true;
    },
    HIDE_TOP_LOADING: (state) => {
      state.SHOW_TOP_LOADING = false;
    },
    SESSION_EXPIRED: (state, action: PayloadAction<boolean>) => {
      state.SESSION_EXPIRED = action.payload;
    },
    SESSION_SUPERSEDED: (state, action: PayloadAction<boolean>) => {
      state.SESSION_SUPERSEDED = action.payload;
    },
    REDIRECT_URL: (state, action: PayloadAction<string>) => {
      state.REDIRECT_URL = action.payload;
    },
    SET_COMPANY_ONBOARDING_STATUS: (state, action: PayloadAction<ICompanyOnboardingStatus>) => {
      state.COMPANY_ONBOARDING_STATUS = action.payload;
    },
    SET_PENDING_ACTIONS: (state, action: PayloadAction<string[]>) => {
      state.PENDING_ACTIONS = action.payload;
    },
    ADD_PENDING_ACTION: (state, action: PayloadAction<string>) => {
      if (!state.PENDING_ACTIONS.includes(action.payload)) {
        state.PENDING_ACTIONS.push(action.payload);
      }
    },
    REMOVE_PENDING_ACTION: (state, action: PayloadAction<string>) => {
      state.PENDING_ACTIONS = state.PENDING_ACTIONS.filter(item => item !== action.payload);
    },
    CLEAR_PENDING_ACTIONS: (state) => {
      state.PENDING_ACTIONS = [];
    },
    SET_TERMS_AND_CONDITIONS_DISMISSED: (state, action: PayloadAction<boolean>) => {
      state.TERMS_AND_CONDITIONS_DISMISSED = action.payload;
    },
    SET_WORKSPACE_SETUP_BANNER_MINIMIZED: (state, action: PayloadAction<boolean>) => {
      state.WORKSPACE_SETUP_BANNER_MINIMIZED = action.payload;
    },
    RESET_GLOBALS: () => {
      return { ...initialState };
    },
  }
});

export const {
  SHOW_TOP_LOADING,
  HIDE_TOP_LOADING,
  SESSION_EXPIRED,
  SESSION_SUPERSEDED,
  REDIRECT_URL,
  SET_PENDING_ACTIONS,
  ADD_PENDING_ACTION,
  REMOVE_PENDING_ACTION,
  CLEAR_PENDING_ACTIONS,
  SET_COMPANY_ONBOARDING_STATUS,
  SET_TERMS_AND_CONDITIONS_DISMISSED,
  SET_WORKSPACE_SETUP_BANNER_MINIMIZED,
  RESET_GLOBALS
} = globalSlice.actions;

export const getTopLoadingState = (state: RootState): boolean => state.globals.SHOW_TOP_LOADING;
export const getSessionStatus = (state: RootState): boolean => state.globals.SESSION_EXPIRED;
export const getSessionSuperseded = (state: RootState): boolean => state.globals.SESSION_SUPERSEDED;
export const getRedirectURL = (state: RootState): string => state.globals.REDIRECT_URL;
export const getPendingActions = (state: RootState): string[] => state.globals.PENDING_ACTIONS;
export const hasPendingActions = (state: RootState): boolean => state.globals.PENDING_ACTIONS.length > 0;
export const getCompanyOnboardingStatusFromState = (state: RootState): ICompanyOnboardingStatus => state.globals.COMPANY_ONBOARDING_STATUS;
export const getTermsAndConditionsDismissed = (state: RootState): boolean => state.globals.TERMS_AND_CONDITIONS_DISMISSED;
export const getWorkspaceSetupBannerMinimized = (state: RootState): boolean => state.globals.WORKSPACE_SETUP_BANNER_MINIMIZED;
export default globalSlice.reducer;
